"use client";

import { useContext, useEffect, useRef, useSyncExternalStore } from "react";

import { InputVisibilityContext } from "../contexts/InputVisibilityContext";

/**
 * Hook for accessing input visibility context.
 * Must be used within an InputVisibilityContext provider (FormTemplate).
 * @throws Error if used outside of a FormTemplate.
 * @returns The InputVisibilityState context.
 */
export const useInputVisibility = () => {
  const context = useContext(InputVisibilityContext);
  if (!context) {
    throw new Error("useInputVisibility must be used within a FormTemplate");
  }
  return context;
};

/**
 * Hook for optionally accessing input visibility context.
 * Returns undefined if not within an InputVisibilityContext provider.
 * Use this when the component may or may not be inside a Form.
 * @returns The InputVisibilityState context, or undefined if not available.
 */
export const useMaybeInputVisibility = () => useContext(InputVisibilityContext);

const emptyVisibilityMap: Record<string, boolean> = {};

/**
 * Hook to subscribe to visibility map changes.
 * Uses useSyncExternalStore for optimal performance.
 * @returns The current visibility map, or an empty object if not in a FormTemplate.
 */
export const useVisibilityMap = () => {
  const context = useMaybeInputVisibility();

  return useSyncExternalStore(
    context?.subscribeToVisibilityChanges ?? (() => () => {}),
    context?.getVisibilitySnapshot ?? (() => emptyVisibilityMap),
    context?.getVisibilitySnapshot ?? (() => emptyVisibilityMap),
  );
};

/**
 * Hook that automatically registers/unregisters visibility state with a parent Form.
 * This handles the lifecycle of visibility registration for FormInputTemplate.
 * @param id - The unique identifier of the form input.
 * @param isVisible - Whether the form input is currently visible.
 */
export const useRegisterInputVisibility = (
  id: string | undefined,
  isVisible: boolean,
) => {
  const context = useMaybeInputVisibility();

  // Use refs to avoid re-running the effect when context reference changes
  const registerRef = useRef(context?.registerInputVisibility);
  const unregisterRef = useRef(context?.unregisterInputVisibility);

  // Keep refs up to date
  registerRef.current = context?.registerInputVisibility;
  unregisterRef.current = context?.unregisterInputVisibility;

  // Register visibility changes
  useEffect(() => {
    if (!registerRef.current || !id) return;
    registerRef.current(id, isVisible);
  }, [id, isVisible]);

  // Unregister on unmount
  useEffect(() => {
    if (!id) return;

    return () => {
      unregisterRef.current?.(id);
    };
  }, [id]);
};
